import * as THREE from 'three';
import { DRONE_MARKER_RESOURCE_RANGE_METERS } from '../../shared/constants.js';
import { simulationToMapPoint } from '../../shared/spatial.js';
import type { RenderingIntent } from '../../shared/renderingIntent.js';
import type { DroneState, GameSnapshot, RoomState } from '../../shared/types.js';
import type { ViewportCameraPolicy } from './hostCameraPolicy.js';
import type { GraphicsQuality } from './quality.js';
import { DEFAULT_RENDER_TUNING, type RenderTuning } from './renderTuning.js';
import { WorldViewportView } from './WorldViewportView.js';
import type { BeamGlowState } from './hostWorldSceneLighting.js';

/**
 * The live picture from the nose of one drone, drawn locally on the pilot's phone with the same
 * world renderer (and quality levels) as the submarine's own cameras.
 *
 * The scene is submarine-centred, so the drone camera is simply placed at the drone's offset from the
 * submarine and turned to the drone's heading.
 */

const CAMERA_MOUNT_FORWARD_METERS = 0.022;
const CAMERA_FOV_DEGREES = 74;
const DRONE_LIGHT_INTENSITY = 26;
const DRONE_LIGHT_DISTANCE = 26;
const DRONE_LIGHT_ANGLE = Math.PI * 0.16;
const DRONE_LIGHT_PENUMBRA = 0.5;
const DRONE_FILL_INTENSITY = 2.4;
const DRONE_FILL_DISTANCE = 9;

type Pose = { offset: THREE.Vector3; yawRadians: number; pitchRadians: number };

const createDroneCameraPolicy = (getPose: () => Pose | null): ViewportCameraPolicy => {
  const place = (camera: THREE.PerspectiveCamera): void => {
    const pose = getPose();
    if (!pose) {
      camera.position.set(0, -1, 0);
      camera.setRotationFromEuler(new THREE.Euler(0, 0, 0, 'YXZ'));
      return;
    }
    camera.position.copy(pose.offset);
    camera.setRotationFromEuler(new THREE.Euler(pose.pitchRadians, -pose.yawRadians, 0, 'YXZ'));
    camera.up.set(0, 1, 0);
  };
  return {
    initializeCamera: (camera: THREE.PerspectiveCamera, _intent: RenderingIntent) => place(camera),
    applyOrientation: ({ camera }: { camera: THREE.PerspectiveCamera } & any) => place(camera),
    applyShake: () => 0
  };
};

export class DroneCameraView {
  /** Exposed (rather than private) so dev tooling can drive and capture frames, e.g. window.__droneCamera in ?debug pages. */
  readonly viewportView: WorldViewportView;
  private readonly spotLight: THREE.SpotLight;
  private readonly lightTarget: THREE.Object3D;
  private readonly fillLight: THREE.PointLight;
  // Registered with the particle system: suspended particles and bubbles are lit by the drone lamp only while it is on.
  private readonly lampGlowState: BeamGlowState = {
    source: new THREE.Vector3(), direction: new THREE.Vector3(0, 0, -1),
    angle: DRONE_LIGHT_ANGLE, range: DRONE_LIGHT_DISTANCE, intensity: 0
  };
  private readonly tempVec = new THREE.Vector3();
  private lastSnapshot: GameSnapshot | null = null;
  private droneId: string | null = null;
  private pose: Pose | null = null;
  private lookYawDegrees: number | null = null;
  private lookPitchDegrees = 0;

  constructor(
    container: HTMLElement,
    quality: GraphicsQuality,
    tuning: RenderTuning = DEFAULT_RENDER_TUNING,
    fpsOverride?: number
  ) {
    const scene = new THREE.Scene();
    this.viewportView = new WorldViewportView(
      container,
      quality,
      tuning,
      createDroneCameraPolicy(() => this.pose),
      scene,
      { autoStartAnimation: true, fpsOverride }
    );
    // The submarine model must show when the drone looks back at it.
    this.viewportView.setPeriscopeSubmarineVisible(true);

    const camera = this.viewportView.getCamera();
    camera.fov = CAMERA_FOV_DEGREES;
    camera.near = 0.01; // the drone is tiny: its camera sits centimetres from its own hull and the repair site
    camera.updateProjectionMatrix();

    this.lightTarget = new THREE.Object3D();
    this.lightTarget.position.set(0, 0, -6);
    camera.add(this.lightTarget);
    this.spotLight = new THREE.SpotLight(0xbfe9ff, 0, DRONE_LIGHT_DISTANCE, DRONE_LIGHT_ANGLE, DRONE_LIGHT_PENUMBRA, 2);
    this.spotLight.position.set(0, 0, 0.005);
    this.spotLight.target = this.lightTarget;
    this.spotLight.castShadow = false;
    camera.add(this.spotLight);

    // Faint fill so the immediate surroundings are never pure black, even with the lights off.
    this.fillLight = new THREE.PointLight(0x7fb8d8, DRONE_FILL_INTENSITY, DRONE_FILL_DISTANCE, 2);
    this.fillLight.position.set(0, 0, 0.015);
    this.fillLight.castShadow = false;
    camera.add(this.fillLight);
    this.viewportView.setExternalParticleGlowSources([this.lampGlowState]);

    this.viewportView.setMarkerLabelOverride(() => this.buildMarkers());
  }

  /** Pause rendering while there is no signal (saves the phone's GPU). */
  setActive(active: boolean): void {
    this.viewportView.setRenderGate(() => active);
  }

  /** Which drone this feed shows (null = no signal). */
  setDroneId(id: string | null): void {
    this.droneId = id;
    this.viewportView.setHiddenDroneId(id);
    this.refreshPose();
  }

  private currentDrone(): DroneState | null {
    if (!this.droneId) return null;
    return this.lastSnapshot?.drones?.find((drone) => drone.id === this.droneId) ?? null;
  }

  private refreshPose(): void {
    const snapshot = this.lastSnapshot;
    const drone = this.currentDrone();
    if (!snapshot || !drone || drone.mode === 'docked') {
      this.pose = null;
      return;
    }
    const sub = simulationToMapPoint({ x: snapshot.submarine.simulationX, y: snapshot.submarine.simulationZ });
    const yawRadians = (((this.lookYawDegrees ?? drone.yaw)) * Math.PI) / 180;
    const forwardX = Math.sin(yawRadians);
    const forwardZ = -Math.cos(yawRadians);
    this.pose = {
      offset: new THREE.Vector3(
        drone.mapX - sub.x + forwardX * CAMERA_MOUNT_FORWARD_METERS,
        snapshot.submarine.depth - drone.depth,
        drone.mapZ - sub.z + forwardZ * CAMERA_MOUNT_FORWARD_METERS
      ),
      yawRadians,
      // While repairing, the camera looks at the damaged spot; otherwise the pilot's own tilt applies.
      pitchRadians: ((drone.lookPitch ?? this.lookPitchDegrees) * Math.PI) / 180
    };
  }

  /**
   * The pilot's finger-drag look direction. `yawDegrees` (null = follow the drone's own heading) and
   * `pitchDegrees` (up positive) apply to the camera immediately, without waiting for the server.
   */
  setLook(yawDegrees: number | null, pitchDegrees: number): void {
    this.lookYawDegrees = yawDegrees;
    this.lookPitchDegrees = pitchDegrees;
    this.refreshPose();
  }

  /** The submarine is marked at any distance; resources only when within DRONE_MARKER_RESOURCE_RANGE_METERS. */
  private buildMarkers() {
    const snapshot = this.lastSnapshot;
    const drone = this.currentDrone();
    if (!snapshot || !drone || drone.mode === 'docked') return null;
    const sub = simulationToMapPoint({ x: snapshot.submarine.simulationX, y: snapshot.submarine.simulationZ });
    const markers: Array<{ key: string; title: string; accent: string; mapX: number; mapZ: number; worldY: number }> = [
      { key: 'drone-sub', title: 'AEGIS 7', accent: '#7fe4f2', mapX: sub.x, mapZ: sub.z, worldY: -snapshot.submarine.depth }
    ];
    for (const node of snapshot.world.resourceNodes ?? []) {
      if (node.resourceValue < 1) continue;
      const point = simulationToMapPoint({ x: node.simulationX, y: node.simulationZ });
      const distance = Math.hypot(point.x - drone.mapX, point.z - drone.mapZ, node.depth - drone.depth);
      if (distance > DRONE_MARKER_RESOURCE_RANGE_METERS) continue;
      markers.push({
        key: `drone-res-${node.id}`,
        title: node.resourceType.toUpperCase(),
        accent: '#7dff9b',
        mapX: point.x,
        mapZ: point.z,
        worldY: -node.depth
      });
    }
    return {
      extraMarkers: markers,
      textOrigin: { mapX: drone.mapX, mapZ: drone.mapZ, worldY: -drone.depth }
    };
  }

  setSnapshot(snapshot: GameSnapshot): void {
    this.lastSnapshot = snapshot;
    this.viewportView.setSnapshot(snapshot);
    this.refreshPose();
    const drone = this.currentDrone();
    this.spotLight.intensity = drone?.lightsOn ? DRONE_LIGHT_INTENSITY : 0;
    const camera = this.viewportView.getCamera();
    camera.updateMatrixWorld(true);
    this.spotLight.getWorldPosition(this.lampGlowState.source);
    this.lightTarget.getWorldPosition(this.tempVec);
    this.lampGlowState.direction.copy(this.tempVec).sub(this.lampGlowState.source).normalize();
    this.lampGlowState.intensity = this.spotLight.intensity > 0 ? THREE.MathUtils.clamp(this.spotLight.intensity * 0.08, 0, 1.2) : 0;
  }

  setRoomState(state: RoomState): void {
    this.viewportView.setRoomState(state);
  }

  setRenderTuning(tuning: RenderTuning): void {
    this.viewportView.setRenderTuning(tuning);
  }

  dispose(): void {
    this.viewportView.dispose();
  }
}
